// The browser's copy of the delivery quote. See `lambda/delivery-zones/utils/quote.mjs` for the
// reasoning; the two are held together by `delivery-quote-cases.json`, which both suites run.
//
// This one is a **preview**. `POST /orders` recomputes everything from the zone table and its
// numbers are what the order records — so if these ever disagree, the shopper sees a corrected
// total on the confirmation page rather than being charged what the browser guessed
// (docs/ordering-flow/README.md §6.2).

export const DELIVERY_PICKUP = "pickup";
export const DELIVERY_HOME = "delivery";

export type DeliveryMethod = typeof DELIVERY_PICKUP | typeof DELIVERY_HOME;

export const QUOTE_ERROR = {
  ZONE_REQUIRED: "ZONE_REQUIRED",
  ZONE_UNKNOWN: "ZONE_UNKNOWN",
  ZONE_INACTIVE: "ZONE_INACTIVE",
  METHOD_UNKNOWN: "METHOD_UNKNOWN",
  PICKUP_DISABLED: "PICKUP_DISABLED",
} as const;

export type QuoteErrorCode = (typeof QUOTE_ERROR)[keyof typeof QUOTE_ERROR];

export type DeliveryZone = {
  id: string;
  municipality: string;
  department?: string;
  zone: string;
  fee_cop: number;
  free_shipping_threshold_cop: number | null;
  eta_days_min: number;
  eta_days_max: number;
  is_active: boolean;
  display_order?: number;
};

export type DeliveryConfig = {
  default_lead_time_days: number;
  price_hold_days: number;
  pickup_enabled: boolean;
  pickup_address: string;
};

export type DeliveryQuote = {
  fee: number;
  free: boolean;
  etaDaysMin: number;
  etaDaysMax: number;
};

export type QuoteResult = DeliveryQuote | { error: QuoteErrorCode };

export const isQuoteError = (result: QuoteResult): result is { error: QuoteErrorCode } =>
  "error" in result;

const toNumber = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const quoteDelivery = ({
  method,
  zone,
  itemsSubtotal = 0,
  pickupEnabled = true,
}: {
  method: string | null | undefined;
  zone: DeliveryZone | null | undefined;
  itemsSubtotal?: number;
  pickupEnabled?: boolean;
}): QuoteResult => {
  if (method === DELIVERY_PICKUP) {
    if (!pickupEnabled) return { error: QUOTE_ERROR.PICKUP_DISABLED };
    return { fee: 0, free: false, etaDaysMin: 0, etaDaysMax: 0 };
  }

  if (method !== DELIVERY_HOME) return { error: QUOTE_ERROR.METHOD_UNKNOWN };
  if (zone === null || zone === undefined) return { error: QUOTE_ERROR.ZONE_REQUIRED };
  if (zone.is_active !== true) return { error: QUOTE_ERROR.ZONE_INACTIVE };

  const threshold = zone.free_shipping_threshold_cop;
  // `0` is a legitimate threshold meaning "always free", so this cannot be a truthiness check.
  const hasThreshold =
    threshold !== null && threshold !== undefined && Number.isFinite(Number(threshold));
  const free = hasThreshold && toNumber(itemsSubtotal) >= Number(threshold);

  return {
    fee: free ? 0 : toNumber(zone.fee_cop),
    free,
    etaDaysMin: toNumber(zone.eta_days_min),
    etaDaysMax: toNumber(zone.eta_days_max),
  };
};

/**
 * The product's own days plus the zone's, which add up rather than replacing one another.
 * `null` when the cart holds nothing with a knowable date.
 */
export const composePromise = ({
  cartLeadTimeDays,
  etaDaysMin,
  etaDaysMax,
}: {
  cartLeadTimeDays: number | null;
  etaDaysMin: number;
  etaDaysMax: number;
}): { daysMin: number; daysMax: number } | null => {
  if (cartLeadTimeDays === null || cartLeadTimeDays === undefined) return null;

  return {
    daysMin: toNumber(cartLeadTimeDays) + toNumber(etaDaysMin),
    daysMax: toNumber(cartLeadTimeDays) + toNumber(etaDaysMax),
  };
};
